import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DeviceMobileIcon, GearSixIcon, WarningIcon } from '@phosphor-icons/react';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PhoneInput,
  StatusChip,
  isCompleteRuPhone,
  type Tone,
} from '@smena/ui';
import { IgnoredChatsDialog } from '../channel-settings/IgnoredChatsDialog';
import { WarmupBlock } from '../channel-settings/WarmupBlock';
import {
  addMaxIgnored,
  connectMaxSession,
  disconnectMaxSession,
  fetchMaxSession,
  fetchMaxDialogs,
  fetchMaxIgnored,
  fetchMaxSessions,
  removeMaxIgnored,
  submitMaxPassword,
  type MaxSession,
  type MaxSessionStatus,
} from './api';

const STATUS_LABEL: Record<MaxSessionStatus, string> = {
  qr_pending: 'ждём скан QR',
  password_required: 'нужен облачный пароль',
  authorized: 'подключён',
  disconnected: 'отключён',
};

const STATUS_TONE: Record<MaxSessionStatus, Tone> = {
  qr_pending: 'amber',
  password_required: 'amber',
  authorized: 'green',
  disconnected: 'red',
};

export function MaxPersonalCard() {
  const qc = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const [phone, setPhone] = useState('');

  const listQ = useQuery({
    queryKey: ['max-personal'],
    queryFn: fetchMaxSessions,
    refetchInterval: 15_000,
  });

  const connectMut = useMutation({
    mutationFn: () => connectMaxSession(phone || undefined),
    onSuccess: () => {
      setConnecting(false);
      setPhone('');
      void qc.invalidateQueries({ queryKey: ['max-personal'] });
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : 'Не удалось начать подключение'),
  });

  const disconnectMut = useMutation({
    mutationFn: disconnectMaxSession,
    onSuccess: () => {
      toast.success('Номер отвязан');
      void qc.invalidateQueries({ queryKey: ['max-personal'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось отвязать'),
  });

  const sessions = listQ.data?.items ?? [];
  const pending = sessions.find((s) => s.status === 'qr_pending' || s.status === 'password_required');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DeviceMobileIcon weight="bold" className="size-4 text-accent-fg" />
          MAX - личный номер
        </CardTitle>
        <p className="text-xs text-muted">
          Пишем гостю первым с обычного номера УК: бот в MAX инициировать диалог не может.
          Номер привязывается как ещё одно устройство - «Настройки → Устройства → Войти по QR-коду».
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        {listQ.data && !listQ.data.available ? (
          <div className="rounded-md border border-line bg-panel2/40 px-3 py-2 text-xs text-muted">
            Шлюз не настроен на этом стенде (<code>MAX_GATEWAY_URL</code>).
          </div>
        ) : null}

        {sessions.length === 0 && listQ.data?.available && !connecting ? (
          <p className="text-muted">Номер не подключён.</p>
        ) : null}

        {sessions.map((s) => (
          <SessionRow
            key={s.sessionId}
            session={s}
            onDisconnect={() => disconnectMut.mutate(s.sessionId)}
            disconnecting={disconnectMut.isPending}
          />
        ))}

        {connecting && !pending ? (
          <form
            className="flex flex-col gap-2 rounded-md border border-line bg-panel2/40 p-3"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              connectMut.mutate();
            }}
          >
            <Label htmlFor="max-phone">Номер УК (необязательно, для наглядности)</Label>
            <PhoneInput id="max-phone" value={phone} onChange={setPhone} />
            {phone && !isCompleteRuPhone(phone) ? (
              <span className="text-[11px] text-red">
                Номер неполный: нужно 11 цифр, начиная с 7. Пока поддерживаем только российские номера.
              </span>
            ) : null}
            <div className="flex gap-2">
              <Button
                type="submit"
                size="sm"
                disabled={connectMut.isPending || (phone.length > 0 && !isCompleteRuPhone(phone))}
              >
                {connectMut.isPending ? 'Получаем QR…' : 'Получить QR'}
              </Button>
              <Button type="button" size="sm" variant="secondary" onClick={() => setConnecting(false)}>
                Отмена
              </Button>
            </div>
          </form>
        ) : null}

        {/* Пока привязка идёт, кнопки нет: форму под ней всё равно блокирует `pending`, и
            нажатие уводило в тупик - ни кнопки, ни формы, выход только перезагрузкой
            страницы. Прервать привязку можно «Отменить» в самой строке (как в TG). */}
        {!connecting && !pending && listQ.data?.available ? (
          <Button size="sm" className="self-start" onClick={() => setConnecting(true)}>
            Подключить номер
          </Button>
        ) : null}

        {/* Информированный opt-in: риск бана несёт клиент, он должен это увидеть до подключения. */}
        <div className="flex gap-2 rounded-md border border-amber/40 bg-amber/10 px-3 py-2 text-xs text-amber">
          <WarningIcon weight="fill" className="mt-0.5 size-3.5 shrink-0" />
          <span>
            Канал работает через официальную мультидевайс-привязку MAX, но массовые рассылки
            приводят к блокировке номера. Шлём только транзакционные сообщения по броням;
            при разрыве сессии канал деградирует в Telegram-ссылку.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function SessionRow({
  session,
  onDisconnect,
  disconnecting,
}: {
  session: MaxSession;
  onDisconnect: () => void;
  disconnecting: boolean;
}) {
  // Привязка идёт прямо сейчас: оператор сканирует QR или вводит пароль.
  const binding = session.status === 'qr_pending' || session.status === 'password_required';

  const [settingsOpen, setSettingsOpen] = useState(false);

  // Пока идёт привязка - поллим статус чаще, чем обновляется список.
  const liveQ = useQuery({
    queryKey: ['max-personal', session.sessionId],
    queryFn: () => fetchMaxSession(session.sessionId),
    enabled: binding,
    refetchInterval: binding ? 2_000 : false,
    initialData: session,
  });

  // Когда привязка кончилась, поллинг выключается - но его последний ответ остаётся в кэше
  // react-query. Читать статус оттуда после этого нельзя: строка застывала в снятой стадии.
  // Живой случай: сокет к MAX умирал на шаге 2FA, список уже отдавал `disconnected`, а в
  // панели висело «нужен облачный пароль» - без поля пароля (оно смотрело в список) и без
  // причины. Список - последняя инстанция; кэш поллинга - только пока он живой.
  const s = binding ? (liveQ.data ?? session) : session;
  const needsQr = s.status === 'qr_pending';
  const needsPassword = s.status === 'password_required';
  const hint = statusHint(s);

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-panel2/40 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate">{s.phone ? `+${s.phone}` : 'номер не указан'}</div>
          <div className="font-mono text-[11px] text-muted">{s.sessionId.slice(0, 8)}…</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StatusChip tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status]}</StatusChip>
          {/* Настройки - только у живого номера: у незавершённой привязки настраивать нечего. */}
          {s.status === 'authorized' ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSettingsOpen(true)}
              aria-label="Настройки номера"
            >
              <GearSixIcon className="size-3.5" />
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            disabled={disconnecting}
            onClick={() => {
              // На незавершённой привязке терять нечего - спрашивать не о чем.
              if (binding || confirm('Отвязать номер? Гостям перестанут уходить сообщения из MAX.')) {
                onDisconnect();
              }
            }}
          >
            {binding ? 'Отменить' : 'Отвязать'}
          </Button>
        </div>
      </div>

      {s.lastError ? <div className="text-[11px] text-destructive">{s.lastError}</div> : null}
      {hint ? <div className="text-[11px] text-muted">{hint}</div> : null}

      {s.status === 'authorized' ? <WarmupBlock state={s.warmupState ?? 'cold'} /> : null}

      {needsQr && s.qr ? <QrBlock image={s.qr.image} expiresAtMs={s.qr.expiresAtMs} /> : null}
      {needsPassword ? <PasswordForm sessionId={s.sessionId} /> : null}

      <IgnoredChatsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        subtitle={s.phone ? `MAX, +${s.phone}` : 'MAX - личный номер'}
        queryKey={['max-personal', s.sessionId, 'ignored']}
        fetchRules={() => fetchMaxIgnored(s.sessionId)}
        addRule={(chatId, note) => addMaxIgnored(s.sessionId, chatId, note)}
        removeRule={(ruleId) => removeMaxIgnored(s.sessionId, ruleId)}
        fetchDialogs={async () => {
          const res = await fetchMaxDialogs(s.sessionId, 100);
          return {
            items: res.items.map((d) => ({
              chatId: d.chatId,
              title: d.title,
              ...(d.phone ? { phone: d.phone } : {}),
            })),
          };
        }}
      />
    </div>
  );
}

/** Что делать оператору в этом статусе. Ошибка шлюза объясняет причину, подсказка - действие. */
function statusHint(s: MaxSession): string | null {
  switch (s.status) {
    case 'password_required':
      return 'Откройте MAX на телефоне, посмотрите облачный пароль и введите его здесь - соединение мы держим.';
    case 'disconnected':
      return 'Номер сейчас не подключён. Нажмите «Подключить номер» и отсканируйте новый QR - понадобится телефон.';
    default:
      return null;
  }
}

function QrBlock({ image, expiresAtMs }: { image: string; expiresAtMs: number }) {
  const expired = expiresAtMs > 0 && Date.now() > expiresAtMs;
  return (
    <div className="flex flex-col items-center gap-2 py-2">
      {/* Белая подложка обязательна: сканеры не читают QR на тёмном фоне. */}
      <img src={image} alt="QR для входа в MAX" className="size-48 rounded-md bg-white p-2" />
      <ol className="list-inside list-decimal text-xs text-muted">
        <li>В приложении MAX: Настройки → Устройства</li>
        <li>«Войти по QR-коду» и наведите камеру сюда</li>
      </ol>
      {expired ? (
        <p className="text-xs text-destructive">QR истёк - нажмите «Подключить номер» заново.</p>
      ) : null}
    </div>
  );
}

function PasswordForm({ sessionId }: { sessionId: string }) {
  const qc = useQueryClient();
  const [password, setPassword] = useState('');
  const mut = useMutation({
    mutationFn: () => submitMaxPassword(sessionId, password),
    onSuccess: () => {
      setPassword('');
      void qc.invalidateQueries({ queryKey: ['max-personal'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Пароль не принят'),
  });

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        mut.mutate();
      }}
    >
      <Label htmlFor={`max-pw-${sessionId}`}>Облачный пароль MAX (2FA)</Label>
      <div className="flex gap-2">
        <Input
          id={`max-pw-${sessionId}`}
          type="password"
          autoComplete="one-time-code"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button type="submit" size="sm" disabled={mut.isPending || !password}>
          {mut.isPending ? 'Проверяем…' : 'Войти'}
        </Button>
      </div>
    </form>
  );
}
